import { JSX, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { Grid } from '@mui/system';
import clsx from 'clsx';
import { countBy, filter, flatMap, isEmpty, isNil, keys, maxBy, minBy, toLower, toString, trim, uniq } from 'lodash';
import { CompanyFundSummary } from 'api';
import { RESET, SYSTEM_COLLAPSE, SYSTEM_EXPAND, USER_EXPAND } from 'common/actions/row-groups/types';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { useFormat } from 'common/hooks';
import { rowsGroupsReducer } from 'common/reducers/rowsGroups';
import { RowsGroups } from 'common/reducers/rowsGroups/types';
import { useStore } from 'common/store';
import { UseFormatValues } from 'common/types/format';
import { ScalarSpreadsheetProps } from 'common/types/scalarSpreadsheet';
import { LayoutContextValues, UseStoreValues } from 'common/types/store';
import { CustomCompactContainer, CustomContainer, CustomScalarSpreadsheet, Tab } from 'components';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import { GridSkeleton } from 'components/Grid';
import { PdfExportTarget } from 'components/PdfExportConfigDialog/hooks/types';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import LayoutContext from 'context/LayoutContext';
import {
  FIRM_SUMMARY_CONTAINER_CLASSNAME,
  FIRM_SUMMARY_KEY,
  FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS,
  FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
  FIRM_SUMMARY_SPREADSHEET_DEFAULT_CURRENCY,
  FIRM_SUMMARY_SPREADSHEET_NON_PARENT_ROW_GROUP,
  FIRM_SUMMARY_TITLE,
  FIRM_SUMMARY_VALUES_SHOWN_PUBLISHED_TITLE,
} from 'pages/Firms/common/constants';
import { useFirmMeasurementDateFilter } from 'pages/Firms/hooks/useFirmMeasurementDateFilter';
import { FirmSummarySpreadsheet, useFirmSummarySheet } from 'pages/Firms/hooks/useFirmSummarySheet';
import { InformationMessage } from 'pages/Funds/components';
import { useGetFirmSummary } from 'services/hooks/firms';
import { generateColumnKey, getArrayValue, getObjectValue, isValidDate } from 'utilities';
import { FirmCompanySummary, FundByCompany } from './useFirmSummaryTab.types';

const useFirmSummaryTab = () => {
  const [storeValue] = useStore() as UseStoreValues;
  const { companyList: currentCompanies, firmInfo: currentFirm } = getObjectValue(storeValue);
  const { id: currentFirmId } = getObjectValue(currentFirm);

  // Handle measurement date filter, query param, and context synchronization
  const {
    hasNoMeasurementDates,
    isLoadingMeasurementDate,
    isMostRecentMeasurementDate,
    mostRecentMeasurementDate,
    selectedMeasurementDate,
  } = useFirmMeasurementDateFilter();

  const {
    data: firmSummary,
    isFetched: isFetchedFirmSummary,
    isFetching: isFetchingFirmSummary,
    isLoading: isLoadingFirmSummary,
  } = useGetFirmSummary({
    firmId: currentFirmId,
    measurementDate:
      !isNil(selectedMeasurementDate) && isValidDate(selectedMeasurementDate.date)
        ? selectedMeasurementDate.date
        : undefined,
    shouldQueryAutomatically: !isNil(currentFirmId) && !isLoadingMeasurementDate,
  });

  const { all_funds: fundNames, companies, firm_gross_irr: firmGrossIRR, funds_gross_irr: fundsGrossIRR } = getObjectValue(firmSummary);

  const [selectedFund, setSelectedFund] = useState<string>(FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS);

  const fundsAvailable = useMemo(() => [FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS, ...getArrayValue(fundNames)], [fundNames]);

  const isAllFundsSelected = useMemo<boolean>(
    () => selectedFund === FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS,
    [selectedFund]
  );

  // The most common fund currency across all funds
  const fundsCurrency = useMemo<string>(
    () =>
      maxBy(
        keys(countBy(flatMap(companies, 'funds'), 'fund_currency')),
        company => countBy(flatMap(companies, 'funds'), 'fund_currency')[company]
      ) ?? FIRM_SUMMARY_SPREADSHEET_DEFAULT_CURRENCY,
    [companies]
  );

  const fundsByCompany = useMemo<FundByCompany[]>(
    () =>
      flatMap(companies, company => {
        const { company_id: companyId, company_name: companyName, funds } = company;

        const currentFund = funds[0];
        const currentCompany: FirmCompanySummary = { company_id: companyId, company_name: companyName };

        // Standalone
        if (funds.length === 1) {
          return [
            {
              ...currentCompany,
              ...currentFund,
              ...FIRM_SUMMARY_SPREADSHEET_NON_PARENT_ROW_GROUP,
            } as FundByCompany,
          ];
        }

        // All fund names for this company, separated by commas
        const fundName: CompanyFundSummary['fund_name'] = funds.map(fund => fund.fund_name).join(', ');
        // The latest most recent measurement date across all funds for this company
        const mostRecentValuationDate: CompanyFundSummary['measurement_date'] = toString(
          maxBy(funds, 'measurement_date')?.measurement_date
        );
        // The earliest initial investment date across all funds for this company
        const initialInvestmentDate: CompanyFundSummary['initial_investment_date'] = toString(
          minBy(funds, 'initial_investment_date')?.initial_investment_date
        );
        // The latest most recent investment date across all funds for this company
        const mostRecentInvestmentDate: CompanyFundSummary['most_recent_investment_date'] = toString(
          maxBy(funds, 'most_recent_investment_date')?.most_recent_investment_date
        );

        // The most common fund currency across all funds for this company
        const companyFundsCurrency = maxBy(
          keys(countBy(funds, 'fund_currency')),
          fund => countBy(funds, 'fund_currency')[fund]
        );

        const companyFund: FundByCompany = {
          ...currentCompany,
          ...currentFund,
          diluted_ownership: null,
          fund_cost_basis: null,
          fund_name: fundName,
          funds_count: funds.length,
          initial_investment_date: initialInvestmentDate,
          is_parent: true,
          most_recent_investment_date: mostRecentInvestmentDate,
          parent_key: null,
          realized_value: null,
          total_value: null,
          unrealized_value: null,
          fund_currency: companyFundsCurrency ?? fundsCurrency,
          measurement_date: mostRecentValuationDate,
          fund_id: 0,
          fund_slug: '',
        };

        const companyFundKey = generateColumnKey({
          id: companyFund.company_name,
          name: companyFund.fund_name,
          prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
        });

        const companyFunds = funds.map(
          fund =>
            ({
              ...currentCompany,
              ...fund,
              ...FIRM_SUMMARY_SPREADSHEET_NON_PARENT_ROW_GROUP,
              parent_key: companyFundKey,
            }) as FundByCompany
        );

        return [companyFund, ...companyFunds] as FundByCompany[];
      }),
    [companies, fundsCurrency]
  );

  const filteredFundsByCompany = useMemo<FundByCompany[]>(() => {
    if (isAllFundsSelected) {
      return fundsByCompany;
    }

    return filter(fundsByCompany, fund => toLower(trim(fund.fund_name)) === toLower(trim(selectedFund)));
  }, [fundsByCompany, isAllFundsSelected, selectedFund]);

  const hasDraftFunds = useMemo<boolean>(
    () => filteredFundsByCompany.some(fund => !fund.is_published && !fund.is_final),
    [filteredFundsByCompany]
  );

  const hasNoCompanies = useMemo<boolean>(
    () => !isNil(currentCompanies) && isEmpty(currentCompanies),
    [currentCompanies]
  );

  const hasNoInvestments = useMemo<boolean>(
    () => hasNoMeasurementDates || (Boolean(isFetchedFirmSummary) && isNil(firmSummary) && isEmpty(fundsByCompany)),
    [firmSummary, fundsByCompany, hasNoMeasurementDates, isFetchedFirmSummary]
  );

  const isLoading = useMemo<boolean>(
    () => isLoadingFirmSummary || isLoadingMeasurementDate,
    [isLoadingFirmSummary, isLoadingMeasurementDate]
  );

  // Spreadsheet

  const rowGroupParentKeys = useMemo<string[]>(
    () => uniq(fundsByCompany.map(item => toString(item.parent_key)).filter(Boolean)),
    [fundsByCompany]
  );

  const rowGroupsInitialState = useMemo<RowsGroups>(
    () =>
      Object.fromEntries(rowGroupParentKeys.map(key => [key, isAllFundsSelected ? SYSTEM_COLLAPSE : SYSTEM_EXPAND])),
    [isAllFundsSelected, rowGroupParentKeys]
  );

  const [rowGroups, setRowGroups] = useReducer(rowsGroupsReducer, rowGroupsInitialState);

  const { spreadsheet: firmSummarySpreadsheet } = useFirmSummarySheet({
    fundsAvailable,
    fundsByCompany: filteredFundsByCompany,
    isAllFundsSelected,
    selectedFund,
    setSelectedFund,
    firmGrossIRR,
    fundsGrossIRR,
  });

  const spreadsheets = useMemo<FirmSummarySpreadsheet[]>(
    () =>
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore: private 'yLength' property
      firmSummarySpreadsheet?.yLength ? [firmSummarySpreadsheet] : [],
    [firmSummarySpreadsheet]
  );

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: private 'shouldValidate' property
  const { onChange, workbook, workbookContextValue } = useWorkbook(spreadsheets);

  const [format, formatDispatch] = useFormat({
    measurementDate: isMostRecentMeasurementDate ? mostRecentMeasurementDate : selectedMeasurementDate,
    page: CAP_TABLE_CURRENCY_PAGE,
    sourceCurrency: fundsCurrency,
    units: REGULAR_UNIT,
  }) as UseFormatValues;

  const firmSummarySpreadsheetProps = useMemo<ScalarSpreadsheetProps | null>(() => {
    if (firmSummarySpreadsheet) {
      return {
        ...firmSummarySpreadsheet,
        data: getArrayValue(firmSummarySpreadsheet?.data),
        format,
        formatDispatch,
        onChange,
        sheet: firmSummarySpreadsheet,
        workbook,
        rowGroups,
        setRowGroups,
      } as unknown as ScalarSpreadsheetProps;
    }

    return null;
  }, [firmSummarySpreadsheet, format, formatDispatch, onChange, rowGroups, workbook]);

  const { isExportMode } = useContext(LayoutContext) as unknown as LayoutContextValues;

  const hasExpandedRowGroup = useMemo(
    () => Object.values(rowGroups).some(rowGroup => [SYSTEM_EXPAND, USER_EXPAND].includes(rowGroup)),
    [rowGroups]
  );

  const firmSummaryTabContent = useMemo<JSX.Element | null>(() => {
    if (hasNoCompanies || hasNoInvestments) {
      return null;
    }

    return (
      <CustomContainer data-ref={PdfExportTarget.FIRM_SUMMARY_TABLE}>
        <Grid className={FIRM_SUMMARY_CONTAINER_CLASSNAME} size={{ xs: 12 }}>
          {isLoading ? (
            // Loading grid skeleton while fetching data
            <GridSkeleton maxColumns={12} maxRows={4} />
          ) : (
            firmSummarySpreadsheetProps &&
            firmSummarySpreadsheet && (
              <WorkbookContext.Provider value={workbookContextValue}>
                <CustomCompactContainer>
                  <Grid
                    className={clsx(FIRM_SUMMARY_CONTAINER_CLASSNAME, { expanded: hasExpandedRowGroup })}
                    size={{ xs: 12 }}>
                    <CustomScalarSpreadsheet {...firmSummarySpreadsheetProps} isLoading={isFetchingFirmSummary} />
                  </Grid>

                  {!isExportMode && (
                    <InformationMessage
                      displayMessage={hasDraftFunds}
                      message={FIRM_SUMMARY_VALUES_SHOWN_PUBLISHED_TITLE}
                    />
                  )}
                </CustomCompactContainer>
              </WorkbookContext.Provider>
            )
          )}
        </Grid>
      </CustomContainer>
    );
  }, [
    firmSummarySpreadsheet,
    firmSummarySpreadsheetProps,
    hasDraftFunds,
    hasExpandedRowGroup,
    hasNoCompanies,
    hasNoInvestments,
    isFetchingFirmSummary,
    isLoading,
    workbookContextValue,
    isExportMode,
  ]);

  // Tab
  const firmSummaryTab = useMemo<Tab>(
    () =>
      ({
        id: FIRM_SUMMARY_KEY,
        label: FIRM_SUMMARY_TITLE,
        content: firmSummaryTabContent,
      }) as Tab,
    [firmSummaryTabContent]
  );

  // Reset selected fund when measurement date changes
  useEffect(() => {
    if (!isLoadingMeasurementDate) {
      setSelectedFund(FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS);
    }
  }, [isLoadingMeasurementDate, selectedMeasurementDate]);

  // Synchronize rowGroups state with dynamic parent keys
  useEffect(() => {
    if (!isEmpty(rowGroupsInitialState)) {
      setRowGroups({ payload: rowGroupsInitialState, type: RESET });
    }
  }, [rowGroupsInitialState]);

  return { firmSummaryTab, hasNoCompanies, hasNoInvestments };
};

export default useFirmSummaryTab;
